import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ChevronLeft, Mail, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser, token } = useAuth();

  const emailParam = searchParams.get('email') || user?.email || '';
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (user?.email_verified) {
      navigate('/prelanzamiento', { replace: true });
    }
  }, [user, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ email: emailParam, code })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error de verificación');

      setVerified(true);
      if (user) {
        updateUser({ ...user, email_verified: true });
      }
      toast.success('Correo verificado exitosamente');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ email: emailParam })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      toast.success('Código reenviado a tu correo');
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  };

  const handleSkip = () => {
    navigate(user?.role === 'creator' ? '/dashboard' : '/prelanzamiento');
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex items-center px-4 py-3 border-b border-gray-100">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 py-12">
        <div className="w-full max-w-sm mx-auto">
          <Link to="/" className="block text-center mb-8">
            <img src="/utentik-logo.png" alt="utentik" className="h-7 mx-auto" />
          </Link>

          {!verified ? (
            <>
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-7 h-7 text-primary" />
              </div>
              <h1 className="font-rubik text-2xl font-bold text-center text-gray-500 mb-2">
                Verifica tu correo
              </h1>
              <p className="text-center text-gray-400 mb-8">
                Enviamos un código a{' '}
                <span className="font-medium text-gray-500">{emailParam}</span>
              </p>

              {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 text-sm mb-6" data-testid="verify-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleVerify} className="space-y-5">
                <div>
                  <Label htmlFor="code" className="text-sm font-medium text-gray-500">Código de verificación</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="text"
                    maxLength={32}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="mt-2 input-airbnb text-center text-2xl tracking-[0.5em] font-mono"
                    required
                    autoFocus
                    data-testid="verify-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || code.length < 6}
                  className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6 font-semibold"
                  data-testid="verify-submit-btn"
                >
                  {loading ? 'Verificando...' : 'Verificar correo'}
                </Button>
              </form>

              <button
                onClick={handleResend}
                disabled={resending}
                className="block mx-auto mt-6 text-sm text-primary font-medium disabled:opacity-50"
                data-testid="resend-verify-btn"
              >
                {resending ? 'Reenviando...' : 'Reenviar código'}
              </button>

              <button
                onClick={handleSkip}
                className="block mx-auto mt-4 text-sm text-gray-400 hover:text-gray-500"
                data-testid="skip-verify-btn"
              >
                Verificar después
              </button>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="font-rubik text-2xl font-bold text-gray-500 mb-2">
                Correo verificado
              </h1>
              <p className="text-gray-400 mb-8">
                Tu cuenta ha sido verificada exitosamente
              </p>
              <Button
                onClick={() => navigate(user?.role === 'creator' ? '/dashboard' : '/')}
                className="w-full bg-primary hover:bg-primary-hover rounded-xl py-6 font-semibold"
                data-testid="go-explore-btn"
              >
                Explorar actividades
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
